import { Response } from '@marketplace-web/core-api/api-client-util'
import {
  ProductItemModel,
  transformItemBoxDto,
  ItemBadgeModel,
  ItemBoxDto,
} from '@marketplace-web/item-box/product-item-data'
import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'

import { GetWardrobeItemsArgs, GetWardrobeItemsResp, WardrobeItemDto } from '../types/wardrobe-item'
import type { PaginationDto, PaginationModel } from '../types/pagination'
import { findThumbnail } from '../utils/photo'

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
  Large = 'thumb310x430',
}

const getItemThumbnail = (
  photos: Array<{
    is_main: boolean
    thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }>
  }>,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ is_main }) => is_main)

  return findThumbnail(photo, size)
}

const getDominantColor = (photos: Array<{ is_main: boolean; dominant_color_opaque: string }>) => {
  if (!photos.length) return null

  const photo = photos.find(({ is_main }) => is_main === true)

  if (!photo) return null

  return photo.dominant_color_opaque
}

const transformWardrobeItemDtoToProductItem = (item: WardrobeItemDto): ProductItemModel => ({
  id: item.id,
  title: item.title,
  url: item.url,
  photos: item.photos.map(photo => ({
    url: photo.url,
    thumbnails: photo.thumbnails,
    isMain: photo.is_main,
    imageNo: photo.image_no,
  })),
  viewCount: item.view_count,
  favouriteCount: item.favourite_count,
  user: { id: item.user.id, isBusiness: item.user.business },
  priceWithDiscount: item.discount_price ? transformCurrencyAmountDto(item.discount_price) : null,
  price: transformCurrencyAmountDto(item.price),
  serviceFee: item.service_fee ? transformCurrencyAmountDto(item.service_fee) : null,
  totalItemPrice: item.total_item_price
    ? transformCurrencyAmountDto(item.total_item_price)
    : undefined,
  thumbnailUrl: getItemThumbnail(item.photos, ItemThumbnailSize.Large),
  dominantColor: getDominantColor(item.photos),
  badge: item.badge as ItemBadgeModel | null,
  itemAlertType: item.item_alert_type,
  itemClosingAction: item.item_closing_action,
  isPromoted: item.promoted,
  isDraft: item.is_draft,
  isReserved: item.is_reserved,
  isHidden: item.is_hidden,
  isClosed: item.is_closed,
  isFavourite: item.is_favourite,
  isProcessing: item.is_processing,
  canPushUp: item.can_push_up,
  canEdit: item.can_edit,
  areStatsVisible: item.stats_visible,
  itemBox: item.item_box && transformItemBoxDto(item.item_box as ItemBoxDto),
  isHeavyBulky: item.is_heavy_bulky,
})

const transformPaginationDto = (dto: PaginationDto): PaginationModel => ({
  currentPage: dto.current_page,
  totalPages: dto.total_pages,
  totalEntries: dto.total_entries,
  perPage: dto.per_page,
  time: dto.time,
})

export const transformWardrobeItemDtosToProductItems = (
  items: Array<WardrobeItemDto>,
): Array<ProductItemModel> => items.map(transformWardrobeItemDtoToProductItem)

export const transformWardrobeItemsResponse = (
  response: Response<GetWardrobeItemsResp>,
): {
  items: Array<ProductItemModel>
  pagination: PaginationModel
} => ({
  items: transformWardrobeItemDtosToProductItems(response.items),
  pagination: transformPaginationDto(response.pagination),
})

export const getWardrobeItemsArgsToParams = ({
  currentPage,
  perPage,
  order,
  filter,
  catalogIds,
  selectedItemId,
}: GetWardrobeItemsArgs) => ({
  page: currentPage + 1,
  per_page: perPage,
  cond: filter,
  order,
  catalog_ids: catalogIds?.join(','),
  selected_item_id: selectedItemId,
})
